// Persistent outbox for bonus photos awaiting upload (IndexedDB).
//
// The captured photo is written here BEFORE the bonus save goes out, under
// a bonusId minted on the device (the server accepts it), so it lives on
// the device — not just in page memory — from the moment Save is tapped.
// A phone locked mid-save, a lost save response or a killed tab no longer
// loses it. Leftover photos are re-queued by Base.jsx (see
// listDrainablePhotos).
//
// An entry carries TWO images and tracks them separately: `blob` is the
// cropped evidence photo and `fullBlob` the wider reference shot. They upload
// as separate requests, so one of them finishing is real progress that must
// not be undone by the other failing.
//
// Every operation is best-effort: if IndexedDB is unavailable (private
// browsing, storage pressure) callers still proceed with the in-memory
// upload — only the crash-recovery guarantee is lost.

import { v4 as uuidv4 } from 'uuid';

const DB_NAME = 'bonusPhotoOutbox';
const DB_VERSION = 1;
const STORE = 'pendingPhotos';

function openDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'bonusId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore(mode, operation) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = operation(tx.objectStore(STORE));
        tx.oncomplete = () => {
          db.close();
          resolve(request.result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error);
        };
      })
  );
}

// Read-modify-write one entry inside a single transaction. `mutate` gets the
// stored entry and returns the replacement, or null to leave it untouched.
function updateEntry(bonusId, mutate) {
  return withStore('readwrite', (store) => {
    const request = store.get(bonusId);
    request.onsuccess = () => {
      if (!request.result) return;
      const next = mutate(request.result);
      if (next) store.put(next);
    };
    return request;
  }).catch(() => {});
}

// A photo parked before its bonus save was confirmed is held back from the
// upload queue for this long: an upload that beats its own save would be
// refused as "Bonus not found" and the photo dropped. Past it, the page that
// took the photo is assumed gone — the upload then either attaches (the save
// did land) or is refused and cleared (it never did).
const UNCONFIRMED_GRACE_MS = 10 * 60 * 1000;

// Backoff between upload cycles for an entry that keeps failing, by attempt
// count. Without this, every drain (mount, resume, 3-minute timer) re-queued a
// stuck photo the moment its last cycle ended — so a photo that could not be
// uploaded occupied the single upload worker almost continuously, and the
// photos taken after it waited behind a queue that never cleared. Capped
// rather than given up on: a connection that comes back mid-shift should still
// get everything out.
const RETRY_BACKOFF_MS = [30 * 1000, 2 * 60 * 1000, 5 * 60 * 1000];
const RETRY_BACKOFF_MAX_MS = 15 * 60 * 1000;

// How many failed READS it takes before the bytes are called gone.
//
// One failed read is not evidence. On WebKit a stored Blob's backing data can
// be temporarily unreachable — the content process was killed and the blob
// bridge has not come back — and read fine minutes later. A device in the field
// proved it: both parts flagged unreadable, and the diagnostic then read 533 KB
// and 2.1 MB straight back off the same entry. Believing that first failure
// cost the bonus its photo record for a photo that was never gone.
//
// So a failed read is a failure like any other: it takes the backoff and tries
// again. Only once the reads keep failing across separate attempts — with the
// schedule above, roughly 20 minutes and usually an app reopen — is the part
// called unreadable and the loss reported.
export const UNREADABLE_GIVE_UP_ATTEMPTS = 5;

export const newBonusId = () => uuidv4();

// Photos are stored as BYTES, not as Blobs.
//
// A Blob put into IndexedDB is persisted by reference to a backing store, and
// WebKit can discard that store when it kills the page's content process —
// which is exactly what iOS does to a backgrounded WebView. The record
// survives and `blob.size` still reports the original number from metadata,
// but the bytes are gone: the upload goes out with an empty body, the server
// parses no fields at all, and it fails identically forever.
//
// An ArrayBuffer is structured-cloned BY VALUE, so the data really is copied
// into the database and cannot be invalidated behind our back.
// Blob.arrayBuffer() is only Safari 14+ / Chrome 76+. This runs inside a
// WebView on whatever phone the employee happens to own, and on an older one
// the method is simply missing — which would throw on every capture and mean
// photos never get stored at all. FileReader has been there forever, so it is
// the fallback rather than an assumption about the device.
export function readBlobBytes(blob) {
  if (blob.arrayBuffer) return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

export async function toStoredBytes(blob) {
  if (!blob) return null;
  const data = await readBlobBytes(blob);
  return { data, type: blob.type || 'image/jpeg' };
}

// Bytes for one part, or null. Reads both the byte form and the legacy Blob
// form, because entries written by earlier builds are still on devices.
export function partSize(entry, part) {
  const stored = part === 'full' ? entry.fullData : entry.cropData;
  if (stored && stored.data) return stored.data.byteLength || 0;
  const legacy = part === 'full' ? entry.fullBlob : entry.blob;
  return (legacy && legacy.size) || 0;
}

export function partBlob(entry, part) {
  const stored = part === 'full' ? entry.fullData : entry.cropData;
  if (stored && stored.data && stored.data.byteLength) {
    return new Blob([stored.data], { type: stored.type || 'image/jpeg' });
  }
  const legacy = part === 'full' ? entry.fullBlob : entry.blob;
  return legacy && legacy.size ? legacy : null;
}

export const backoffForAttempt = (attempts) =>
  RETRY_BACKOFF_MS[attempts - 1] || RETRY_BACKOFF_MAX_MS;

// FOUR entry shapes exist on devices in the field, and every one of them has
// to keep uploading — a phone only picks up a new build when its page reloads,
// so an entry can be months older than the code reading it. Always go through
// partSize / partBlob rather than touching these fields directly:
//
//   bd052ed  Jul 8   { bonusId, phone, displayName, blob }        crop only
//   edb5855  Sep 17  { …, blob, fullBlob }                        two Blobs
//   560447e  Sep 25  { …, blob, fullBlob, saved }                 + saved flag
//   current          { …, cropData, fullData, saved }             bytes by value
//
// The three legacy shapes hold Blobs, which is the form WebKit can invalidate
// (see toStoredBytes). They still upload while their bytes survive; readPartBytes
// is what notices when they haven't.
//
// entry: { bonusId, phone, displayName, cropData, fullData, saved }
// fullBlob is the uncropped frame; entries queued before it existed lack it.
// saved:false until the server confirms the bonus; older entries lack the
// flag and were always written after the save.
export function addPendingPhoto(entry) {
  return withStore('readwrite', (store) =>
    store.put({ ...entry, queuedAt: Date.now(), attempts: 0 })
  );
}

export function removePendingPhoto(bonusId) {
  return withStore('readwrite', (store) => store.delete(bonusId)).catch(
    () => {}
  );
}

export function markPhotoSaved(bonusId) {
  return updateEntry(bonusId, (entry) => ({ ...entry, saved: true }));
}

// One of the two images is now on the server. The blob is dropped with the
// flag: it is the largest thing in the entry and keeping it would hold image
// data in storage (and in every listing that reads the entry back) for an
// upload that is already done.
// The bytes for this part could not be read back. Park it — do NOT delete it.
//
// Parking stops it blocking the employee at the shift-end gate and stops the
// pointless retries, while keeping whatever is in the record. If the diagnosis
// is wrong, or a later build can read it, the data is still there. Deleting on
// a suspicion would destroy the only copy of an audit photo.
export function markPartUnreadable(bonusId, part) {
  return updateEntry(bonusId, (entry) =>
    part === 'full'
      ? { ...entry, fullUnreadable: true }
      : { ...entry, cropUnreadable: true }
  );
}

// The bytes read back fine after all. Drop the flag, so nothing downstream —
// the parts list, the shift-end gate, the diagnostic — goes on describing a
// readable photo as lost.
export function clearPartUnreadable(bonusId, part) {
  return updateEntry(bonusId, (entry) =>
    part === 'full'
      ? { ...entry, fullUnreadable: false }
      : { ...entry, cropUnreadable: false }
  );
}

export function markPartUploaded(bonusId, part) {
  return updateEntry(bonusId, (entry) =>
    part === 'full'
      ? { ...entry, fullDone: true, fullData: null, fullBlob: null }
      : { ...entry, cropDone: true, cropData: null, blob: null }
  );
}

// An upload cycle failed. Push the next attempt out so the drains stop
// re-queueing it immediately.
// `reason` is stored so a stuck photo can say WHY off the device — a phone has
// no console to check, and "delayed" covers five different failures that need
// five different fixes.
export function recordAttemptFailure(bonusId, reason) {
  return updateEntry(bonusId, (entry) => {
    const attempts = (entry.attempts || 0) + 1;
    return {
      ...entry,
      attempts,
      nextAttemptAt: Date.now() + backoffForAttempt(attempts),
      lastFailedAt: Date.now(),
      lastError: reason || entry.lastError || null,
    };
  });
}

// Drops the WAIT on everything still unsent, so an explicit "try now" from the
// employee is honoured immediately instead of sitting out a penalty earned
// before they walked back into wifi range.
//
// `attempts` is deliberately left alone. It is not part of the wait — it is the
// record of how badly this photo is failing, and `stalled` (and therefore the
// "End shift anyway" escape hatch) is derived from it. Clearing it here meant
// every gate open reset the count to zero, so it could never reach the stalled
// threshold and an employee with a genuinely unuploadable photo had no way off
// the clock but a manager force-clock-out.
export function clearAllBackoff() {
  return listPendingPhotos()
    .then((photos) =>
      Promise.all(
        photos.map((p) =>
          updateEntry(p.bonusId, (entry) => ({ ...entry, nextAttemptAt: null }))
        )
      )
    )
    .catch(() => {});
}

export function listPendingPhotos() {
  return withStore('readonly', (store) => store.getAll()).catch(() => []);
}

// True while this part still holds an image that should be attempted.
//
// A part flagged unreadable is still attempted while the entry has attempts
// left, because the flag records a read that failed, not bytes that are gone,
// and re-reading is the only way to tell those apart. Entries already carrying
// the flag from an earlier build therefore heal on the next drain.
export const partPending = (entry, part) => {
  const done = part === 'full' ? entry.fullDone : entry.cropDone;
  if (done) return false;
  if (partSize(entry, part) <= 0) return false;
  const flagged = part === 'full' ? entry.fullUnreadable : entry.cropUnreadable;
  if (!flagged) return true;
  return (entry.attempts || 0) < UNREADABLE_GIVE_UP_ATTEMPTS;
};

// True while any part of this entry still has an image waiting to go out.
export const hasWorkLeft = (entry) =>
  Boolean(partPending(entry, 'crop') || partPending(entry, 'full'));

// Outbox entries that may be uploaded now: the bonus save is confirmed (or
// long enough has passed that this page is assumed gone), the backoff has
// elapsed, and there is still something to send.
export function listDrainablePhotos() {
  const now = Date.now();
  const graceCutoff = now - UNCONFIRMED_GRACE_MS;
  return listPendingPhotos().then((photos) =>
    photos.filter(
      (p) =>
        (p.saved !== false || (p.queuedAt || 0) < graceCutoff) &&
        (!p.nextAttemptAt || p.nextAttemptAt <= now) &&
        hasWorkLeft(p)
    )
  );
}

// What the employee is shown, and what the shift-end gate reads. Counts every
// entry with work left — including ones inside a backoff window, because from
// the employee's point of view those are still unsent.
export function summarizePendingPhotos() {
  return listPendingPhotos().then((photos) => {
    const pending = photos.filter(hasWorkLeft);
    return {
      count: pending.length,
      bonusIds: pending.map((p) => p.bonusId),
      // Nothing has got through for a while and attempts keep failing: the
      // shift-end gate offers its escape hatch on the back of this.
      stalled: pending.filter((p) => (p.attempts || 0) >= 2).length,
    };
  });
}
